<template>
  <div class="p-5 pt-1">
    <pop-up
      :active.sync="popUp"
      @close="$router.push({ name: 'VesselLineUpList' })"
      @ok="$router.push({ name: 'VesselLineUpList' })"
    >
      You have successfully update royalty payment
    </pop-up>
    <p class="font-semibold text-3xl">
      Tentative Barge Schedule
    </p>
    <div class="mt-6">
      <div class="bg-white rounded-2xl p-6">
        <div class="flex flex-row flex-wrap items-center border-b pb-5 border-grey border-dashed">
          <img
            class="icon-shadow"
            :src="require('@/assets/shipo-icon-ship.svg')"
          >
          <div class="flex flex-col ml-4">
            <span class="text-primary font-semibold text-xl">{{ tentativeBargeSchedules.name_of_vessel }}</span>
            <span class="text-grey-001 mt-1">{{ tentativeBargeSchedules.shipper }}</span>
          </div>
          <div class=" ml-4 flex items-center">
            <vs-button
              v-if="!isEditingDetails"
              class="ml-auto"
              color="primary"
              @click="isEditingDetails = true"
            >
              Edit Details
            </vs-button>
            <vs-button 
              v-if="isEditingDetails"
              class="ml-auto"
              color="primary"
              @click="submit"
            >
              Save Details
            </vs-button>
            <vs-button 
              v-if="isEditingDetails"
              class="ml-4"
              color="secondary"
              @click="cancelEdit"
            >
              Cancel
            </vs-button>
          </div>
          <span class="ml-auto mr-4 font-semibold text-base">Cargo on Board</span>
          <autonumeric
            class="min-w-cargo disabled:bg-white"
            :disabled="!isEditingDetails"
            v-model="editableFields.cargo_on_board"
            :options="cargoOnBoardOptions"
            placeholder="Enter cargo on board"
          >
            <div class="h-full px-5 flex flex-col justify-center rounded-r-lg bg-primary">
              <span class="text-white">mt</span>
            </div>
          </autonumeric>
        </div>
        <div class="mt-4 flex flex-wrap mx-min-3">
          <!-- Arrival Date -->
          <div class="flex flex-row w-1/4 px-3 mt-4 items-start">
            <img
              class="icon-shadow"
              :src="require('@/assets/shipo-icon-airplane.svg')"
            >
            <div class="ml-6 flex flex-col w-full">
              <span class="text-grey-001 mt-1">Arrival Date</span>
              <span class="text-grey font-semibold mt-1">{{ putOutOrFormatValue(tentativeBargeSchedules.actual_time_arrival) }}</span>
            </div>
          </div>

          <!-- End User -->
          <div class="flex flex-row w-1/4 px-3 mt-4 items-start">
            <img
              class="icon-shadow"
              :src="require('@/assets/shipo-icon-truck.svg')"
            >
            <div class="ml-6 flex flex-col w-full">
              <span class="text-grey-001 mt-1">End User</span>
              <span 
                class="text-grey font-semibold mt-1"
              >
                {{ putOutOrFormatValue(tentativeBargeSchedules.end_user) }}
              </span>
            </div>
          </div>

          <!-- Load Facilities -->
          <div class="flex flex-row w-1/4 px-3 mt-4 items-start">
            <img
              class="icon-shadow"
              :src="require('@/assets/shipo-icon-radar.svg')"
            >
            <div class="ml-6 flex flex-col w-full">
              <span class="text-grey-001 mt-1">Load Facilities</span>
              <select-with-search
                v-if="isEditingDetails"
                :options="loadingFacilities"
                v-model="editableFields.loading_facility"
                placeholder="Select Load Facilities"
              />
              <span 
                v-else
                class="text-grey font-semibold mt-1"
              >
                {{ putOutOrFormatValue(tentativeBargeSchedules.loading_facility) }}
              </span>
            </div>
          </div>

          <!-- Vessel Agent -->
          <div class="flex flex-row w-1/4 px-3 mt-4 items-start">
            <img
              class="icon-shadow"
              :src="require('@/assets/shipo-icon-personal-card.svg')"
            >
            <div class="ml-6 flex flex-col w-full">
              <span class="text-grey-001 mt-1">Vessel Agent</span>
              <select-with-search
                v-if="isEditingDetails"
                class="mt-1"
                :options="agencies"
                v-model="editableFields.agency"
                placeholder="Select Vessel Agent"
              />
              <span 
                v-else
                class="text-grey font-semibold mt-1"
              >
                {{ putOutOrFormatValue(tentativeBargeSchedules.agency) }}
              </span>
            </div>
          </div>

          <!-- LayCan of Vessel -->
          <div class="flex flex-row w-1/4 px-3 mt-4 items-start">
            <img
              class="icon-shadow"
              :src="require('@/assets/shipo-icon-menu-board.svg')"
            >
            <div class="ml-6 flex flex-col w-full">
              <span class="text-grey-001 mt-1">LayCan of Vessel</span>
              <span class="text-grey font-semibold mt-1">{{ putOutOrFormatValue(tentativeBargeSchedules.laycan_of_vessel) }}</span>
            </div>
          </div>

          <!-- Shipper Rep. -->
          <div class="flex flex-row w-1/4 px-3 mt-4 items-start">
            <img
              class="icon-shadow"
              :src="require('@/assets/shipo-icon-profile-user.svg')"
            >
            <div class="ml-6 flex flex-col w-full">
              <span class="text-grey-001 mt-1">Shipper Rep.</span>
              <select-with-search
                v-if="isEditingDetails"
                class="mt-1"
                :options="shipperRep"
                v-model="editableFields.shipper_rep"
                placeholder="Select Shipper Rep."
              />
              <span 
                v-else
                class="text-grey font-semibold mt-1"
              >
                {{ tentativeBargeSchedules.tentative_barge_schedule_header? tentativeBargeSchedules.tentative_barge_schedule_header.shipper_rep : '-' }}
              </span>
            </div>
          </div>

          <!-- FLF/FC Nominate -->
          <div class="flex flex-row w-1/4 px-3 mt-4 items-start">
            <img
              class="icon-shadow"
              :src="require('@/assets/shipo-icon-award.svg')"
            >
            <div class="ml-6 flex flex-col w-full">
              <span class="text-grey-001 mt-1">FLF/FC Nominate</span>
              <select-with-search
                v-if="isEditingDetails"
                class="mt-1"
                :options="flfFcNominates"
                v-model="editableFields.flf_fc_nominate"
                placeholder="Select FLF/FC Nominate"
              />
              <span 
                v-else
                class="text-grey font-semibold mt-1"
              >
                {{ putOutOrFormatValue(tentativeBargeSchedules.flf_fc_nominate) }}
              </span>
            </div>
          </div>

          <!-- 3 Days LayCan (conditional) -->
          <div 
            v-if="editableFields.loading_facility === 2"
            class="flex flex-row w-1/4 px-3 mt-4 items-start"
          >
            <img
              class="icon-shadow"
              :src="require('@/assets/shipo-icon-calendar-time.svg')"
            >
            <div class="ml-6 flex flex-col w-full">
              <span class="text-grey-001 mt-1">3 Days LayCan</span>
              <date-picker
                v-if="isEditingDetails"
                class="mt-1"
                v-model="editableFields.laycan_3_days_range"
                type="date"
                range
                placeholder="Select date range"
                :custom-disabled-date="customDisabledDate"
                format="DD-MMM-YYYY"
                value-type="format"
              />
              <span 
                v-else
                class="text-grey font-semibold mt-1"
              >
                {{ getThreeDaysLayCan() }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col bg-white rounded-2xl mt-6 p-5">
      <div class=" flex flex-col rounded-md">
        <table-top
          is-search-has-border
          class="mt-6"
          :page-size-start="false"
          :search-self-start="true"
          :hide-page-limit-selection="true"
          @fetch="getLoadingPlans"
          ref="tabletop"
          :default-search="null"
        >
          <template #start>
            <div class="grid grid-cols-2 row-gap-3">
              <vs-button
                :disabled="isEditingTbs || !isUserCanAccessMenu(permission.name, 'add')"
                class="button font-semibold ml-5 bg-primary-accent"
                @click="editDataTbs"
              >
                Edit TBS Data
              </vs-button>
              <vs-button
                v-show="true"
                class="button font-semibold ml-5"
                :class="isOnExportTBS && 'animate-bounce'"
                color="secondary"
                @click="onExportTBS"
                :disabled="!tbsListGetter.length || isOnExportTBS"
              >
                Export TBS
              </vs-button>
              <vs-button
                v-for="tab in tabs"
                :key="tab"
                :disabled="isEditingTbs && tab === 'Loading Plan'"
                :type="isTabActive(tab) ? 'filled' : 'border'"
                class="font-semibold ml-5 h-8 py-0 rounded-3xl"
                color="primary"
                @click="changeTabTo(tab)"
              >
                {{ tab }}
              </vs-button>
            </div>
          </template>
        </table-top>
      </div>
      <data-table
        v-if="activeTab === 'Loading Plan'"
        ref="dataTable"
        :loading="isLoading"
        class="mt-6"
        :table-header="tableHeaders"
        :table-data="tableData"
        :grouped-headers="groupHeaders"
        :hide-search="!tableData.length"
        body-height="100vh"
      >
        <template #no="{ index }">
          <span>{{ index + 1 }}</span>
        </template>
        <template #action="{ row }">
          <div
            class="self-center flex items-center justify-center icon-transparent-bg mx-8"
            @click="onHandleEdit(row)"
          >
            <img :src="require('@/assets/shipo-icon-edit.svg')">
          </div>
        </template>
      </data-table>
      <tbs-section
        v-if="activeTab === 'TBS Data'"
        :is-edit-mode-prop="isEditingTbs"
        :loading-plan-list="this.tbs.loadingPlanList"
        :tbs-id="this.$route.params.id || this.vesselLineUpId"
        :on-cancel-edit="this.onCancelEdit"
        :tbs-header-id="this.tentativeBargeSchedules.tentative_barge_schedule_header ? this.tentativeBargeSchedules.tentative_barge_schedule_header.id : this.tentativeBargeSchedules.id || null"
      />
    </div>
    
    <!-- Modal Edit TBS -->
    <modal-edit-tbs
      v-if="showModalEditTbs"
      @close="() => {
        showModalEditTbs = false
        getLoadingPlans()
      }"
      :active="showModalEditTbs"
      :row-data="modalEditTbs"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import Autonumeric from '@/components/autonumeric'
import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import PopUp from '@/components/popUp'
import SelectWithSearch from '@/components/selectWithSearch'
import TableTop from '@/components/tableTop/index.vue'
import { PAGE_TITLE } from '@/fixtures/Constants'
import TentativeBargeSchedule from '@/fixtures/vessel-line-up-tbs.json'
import downloadUtils from '@/utils/downloadUtils'
import { isNumber } from '@/utils/salesPlanUtils'

import ModalEditTbs from './components/ModalEditTbs.vue'
import TbsSection from './components/TbsSection.vue'

export default {
  metaInfo: {
    title: `${PAGE_TITLE} Tentative Barge Schedule`
  },
  components: {
    TableTop,
    Autonumeric,
    DataTable,
    DatePicker,
    PopUp,
    TbsSection,
    SelectWithSearch,
    ModalEditTbs
  },
  data() {
    return {
      isOnExportTBS: false,
      TentativeBargeSchedule,
      card: {},
      options: {
        shipper_rep: [],
        loading_facility: [],
        flf_fc_nominate: [],
        agency: []
      },
      tabs: ['Loading Plan', 'TBS Data'],
      activeTab: 'Loading Plan', // Default is showing Loading Plan
      dataCargo: {
        total_cargo_supply: 0,
        stowage_plan: 0,
        balance_cargo_on_board: 0,
        remaining_cargo: 0
      },
      tbs: {
        tentativeBargeSchedules: {
          loss_gain_cargo: 0,
          remark_tbs: ''
        },
        bargeLineUp: {
          blending_scheme_id: {},
          royalty: {},
          lhv: {},
          sp: {},
          status: {}
        },
        loadingPlanList: []
      },
      isLoading: false,
      editableFields: {
        cargo_on_board: 0,
        shipper_rep: null,
        loading_facility: null,
        flf_fc_nominate: null,
        agency: null,
        laycan_3_days_start: null,
        laycan_3_days_end: null,
        laycan_3_days_range: null
      },
      isLaycanRangeValid: true,
      isDetailLoading: false,
      isEditingDetails: false,
      isEditingTbs: false,
      tableHeaders: [
        { key: 'no', label: 'No', width: '50px' },
        { key: 'action', label: 'Action', width: '60px' },
        { key: 'blending_scheme', label: 'Blending Scheme', sortable: true },
        { key: 'trip_number', label: 'Trip Number', sortable: true },
        { key: 'tug', label: 'Tug Name', sortable: true },
        { key: 'barge', label: 'Barge Name', sortable: true },
        { key: 'actual_quantity', label: 'Quantity', sortable: true },
        { key: 'product_id', label: 'Product ID', sortable: true },
        { key: 'loading_facility', label: 'Loading Facility', group: 'flf' },
        { key: 'nominate', label: 'Nominate', group: 'flf' },
        { key: 'agency', label: 'Vessel Agent' }
      ],
      groupHeaders: [
        {
          key: 'flf',
          title: 'FLF',
          startColumn: 'loading_facility',
          endColumn: 'nominate',
          backgroundColor: '#F8F8F8'
        }
      ],
      tableData: [],
      popUp: false,
      cargoOnBoardOptions: {
        digitGroupSeparator: ',',
        decimalCharacter: '.',
        decimalCharacterAlternative: ',',
        roundingMethod: 'U',
        minimumValue: '0'
      },
      lossGainCargoptions: {
        allowDecimalPadding: false,
        digitGroupSeparator: '.',
        decimalCharacter: ',',
        decimalCharacterAlternative: '.',
        roundingMethod: 'U'
      },
      statusOptions: [
        { value: 'Transit', text: 'Transit' },
        { value: 'Waiting To Load', text: 'Waiting To Load' },
        { value: 'Waiting At None', text: 'Waiting At None' },
        { value: 'Return Cargo', text: 'Return Cargo' },
        { value: 'Loading', text: 'Loading' },
        { value: 'Loaded Transit', text: 'Loaded Transit' },
        { value: 'Waiting Unload', text: 'Waiting Unload' },
        { value: 'Unloaded Transit', text: 'Unloaded Transit' },
        { value: 'Unloading', text: 'Unloading' },
        { value: 'Completed', text: 'Completed' }
      ],
      reason: null,
      showModalEditTbs: false,
      modalEditTbs: null
    }
  },
  mounted() {
    this.isDetailLoading = true
    this.$store.dispatch('VesselLineUp/setVesselLineUpId', this.$route.params.id || this.vesselLineUpId)
    this.getVesselLineUpDetail()
    this.getTentativeBargeSchedules()
    this.getBlendingSchemes()
    this.getLoadingPlans()
    this.getAgencies()
    this.getLoadingFacilities()
    this.getShipperRep()
    this.fetchTbsList()
    this.isDetailLoading = false
  },
  computed: {
    ...mapGetters({
      vesselLineUpId: 'VesselLineUp/vesselLineUpId',
      vesselLineUpDetail: 'VesselLineUp/vesselLineUpDetail',
      tentativeBargeSchedules: 'TentativeBargeSchedule/tentativeBargeSchedules',
      bargeLineUp: 'VesselLineUp/barges',
      loadingPlans: 'TentativeBargeSchedule/loadingPlans',
      blendingSchemes: 'VesselLineUp/blendingSchemes',
      flfFcNominates: 'VesselLineUp/flfFcNominates',
      agencies: 'VesselLineUp/agencies',
      loadingFacilities: 'VesselLineUp/loadingFacilities',
      shipperRep: 'TentativeBargeSchedule/shipperRep',
      exportFile: 'TentativeBargeSchedule/exportFile',
      tbsListGetter: 'TentativeBargeSchedule/tentativeBargeScheduleDetails'
    }),
    laycanMinDate() {
      if (!this.tentativeBargeSchedules.laycan_of_vessel) return null

      const laycanParts = this.tentativeBargeSchedules.laycan_of_vessel.split(' to ')
      if (laycanParts.length >= 1) {
        return this.moment(laycanParts[0], 'DD-MMM-YYYY').format('YYYY-MM-DD')
      }
      return null
    },
    laycanMaxDate() {
      if (!this.tentativeBargeSchedules.laycan_of_vessel) return null

      const laycanParts = this.tentativeBargeSchedules.laycan_of_vessel.split(' to ')
      if (laycanParts.length >= 2) {
        return this.moment(laycanParts[1], 'DD-MMM-YYYY').format('YYYY-MM-DD')
      }
      return null
    },
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  watch: {
    loadingPlans: {
      handler(val) {
        if(val) {
          this.tableData = val.loading_plan.map((lp, index, arrLp) => {
            const prev = arrLp[index - 1]

            let blending
            if(lp.blending_scheme_spec === 'BLENDING') {
              blending = `${lp.blending_scheme_spec} ${lp.blending_scheme_ratio}` || null
            } else {
              blending = `${lp.blending_scheme_spec}` || null
            }
            return ({
              blending_scheme: blending || null,
              tug: lp.blu_data.tug || null,
              barge: lp.blu_data.barge || null,
              trip_number: lp.blu_data.trip_number || null,
              actual_quantity: lp.blu_data.actual_tonnage || null,
              product_id: lp.product || null,
              loading_facility: lp.loading_facility?.name || null,
              nominate: lp.flf_fc_nominate?.name || null,
              agency: lp.agency?.name || null,
              isGroupStart: !prev || prev.blending_scheme_group !== lp.blending_scheme_group,
              blending_scheme_group: lp.blending_scheme_group || null
            })
          })

          this.tbs.loadingPlanList = [...val.loading_plan]
        }
      }
    },
    isDetailLoading: {
      handler(val) {
        if (val) {
          this.showPageLoading(this)
          return
        }

        this.closePageLoading(this)
      }
    },
    tentativeBargeSchedules: {
      immediate: true,
      async handler(val) {
        if (!this.isEmpty(val)) {
          await this.initializeEditableFields(val)
        }
      }
    },
    bargeLineUp: {
      immediate: true,
      handler(val) {
        this.tableData = val
        if (!this.isEmpty(val)) {
          this.dataCargo.total_cargo_supply = val.reduce((sum, item) => sum + (item.actual_quantity || item.plan_quantity), 0)
          this.updateDataCargo()
          val.forEach(item => {
            this.tbs.bargeLineUp.blending_scheme_id[item.id] = item.blending_scheme_id
            this.tbs.bargeLineUp.royalty[item.id] = item.royalty
            this.tbs.bargeLineUp.sp[item.id] = item.sp
            this.tbs.bargeLineUp.lhv[item.id] = item.lhv
            this.tbs.bargeLineUp.status[item.id] = this.getValueStatus(item.status)
          })
        }
      }
    },
    blendingSchemes: {
      immediate: true,
      handler(val) {
        if (!this.isEmpty(val)) {
          this.options = val
        }
      }
    },
    'editableFields.loading_facility': {
      async handler(newVal) {
        if (newVal) {
          this.editableFields.flf_fc_nominate = null
          await this.getFlfFcNominates()
        }
      }
    },
    'editableFields.laycan_3_days_range': {
      handler(newRange) {
        if (newRange && Array.isArray(newRange) && newRange.length === 2) {
          this.editableFields.laycan_3_days_start = this.moment(newRange[0], 'DD-MMM-YYYY').format('YYYY-MM-DD')
          this.editableFields.laycan_3_days_end = this.moment(newRange[1], 'DD-MMM-YYYY').format('YYYY-MM-DD')
          this.isLaycanRangeValid = this.validateThreeDaysRange(newRange)
        } else {
          this.editableFields.laycan_3_days_start = null
          this.editableFields.laycan_3_days_end = null
          this.isLaycanRangeValid = true
        }
      }
    },
    exportFile: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(
            val,
            `TBS ${this.vesselLineUpDetail.destination} ${this.vesselLineUpDetail.name_of_vessel}.xlsx`
          )
          this.$store.dispatch('TentativeBargeSchedule/resetExportFiles');
        }
      }
    }
  },
  methods: {
    async fetchTbsList() {
      try {
        this.isOnExportTBS = true;
        await this.$store.dispatch('TentativeBargeSchedule/fetchTentativeBargeScheduleDetails', this.$route.params.id || this.vesselLineUpId)
        this.isOnExportTBS = false
      } catch (e) {
        this.isOnExportTBS = false
        this.showFailedNotification(this, 'Failed to fetch TBS List, please try again')
      }
    },
    async getVesselLineUpDetail() {
      try {
        await this.$store.dispatch('VesselLineUp/getVesselLineUpDetail', this.$route.params.id || this.vesselLineUpId)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch VLU detail, please try again')
      }
    },
    async getLoadingPlans() {
      try {
        this.isLoading = true
        await this.$store.dispatch('TentativeBargeSchedule/fetchTentativeBargeSchedulesLoadingPlans', this.$route.params.id || this.vesselLineUpId)
        this.isLoading = false
      } catch (e) {
        this.isLoading = false
        this.showFailedNotification(this, 'Failed to fetch Tentative Barge Schedule, please try again')
      }
    },
    async getTentativeBargeSchedules() {
      try {
        await this.$store.dispatch('TentativeBargeSchedule/fetchTentativeBargeSchedules', this.$route.params.id || this.vesselLineUpId)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Tentative Barge Schedule, please try again')
      }
    },
    async getBargeLineUp(nameOfVessel) {
      try {
        await this.$store.dispatch('VesselLineUp/getBarges', { name_of_vessel: nameOfVessel })
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch BLU, please try again')
      }
    },
    async getBlendingSchemes() {
      await this.$store.dispatch('VesselLineUp/getBlendingSchemes')
    },
    async getFlfFcNominates() {
      await this.$store.dispatch('VesselLineUp/getFlfFcNominatesByLoadingFacility', this.editableFields.loading_facility)
    },
    async getAgencies() {
      await this.$store.dispatch('VesselLineUp/getAgencies')
      await this.initializeEditableFields(this.tentativeBargeSchedules)
    },
    async getLoadingFacilities() {
      await this.$store.dispatch('VesselLineUp/getLoadingFacilities')
      await this.initializeEditableFields(this.tentativeBargeSchedules)
    },
    async getShipperRep() {
      await this.$store.dispatch('TentativeBargeSchedule/getShipperRep')
      await this.initializeEditableFields(this.tentativeBargeSchedules)
    },
    async initializeEditableFields(val) {
      this.editableFields.cargo_on_board = val.tentative_barge_schedule_header?.cargo_on_board || 0
      this.editableFields.shipper_rep = val.tentative_barge_schedule_header?.shipper_rep || ''
      
      this.editableFields.loading_facility = this.findIdByText(val.loading_facility, this.loadingFacilities)
      this.editableFields.agency = this.findIdByText(val.agency, this.agencies)
      
      if (this.editableFields.loading_facility) {
        await this.getFlfFcNominates()
        this.editableFields.flf_fc_nominate = this.findIdByText(val.flf_fc_nominate, this.flfFcNominates)
      }
      
      this.editableFields.laycan_3_days_start = val.tentative_barge_schedule_header?.laycan_3_days_start || null
      this.editableFields.laycan_3_days_end = val.tentative_barge_schedule_header?.laycan_3_days_end || null
      
      if (this.editableFields.laycan_3_days_start && this.editableFields.laycan_3_days_end) {
        this.editableFields.laycan_3_days_range = [
          this.moment(this.editableFields.laycan_3_days_start).format('DD-MMM-YYYY'),
          this.moment(this.editableFields.laycan_3_days_end).format('DD-MMM-YYYY')
        ]
      } else {
        this.editableFields.laycan_3_days_range = null
      }
    },
    findIdByText(textValue, optionsArray) {
      if (!textValue || !optionsArray || !Array.isArray(optionsArray)) {
        return null
      }
      
      if (typeof textValue === 'object' && textValue.id) {
        return textValue.id
      }
      
      const foundOption = optionsArray.find(option => option.text === textValue)
      
      return foundOption ? foundOption.value : null
    },
    customDisabledDate(date) {
      if (!this.tentativeBargeSchedules.laycan_of_vessel) return false
      
      const laycanParts = this.tentativeBargeSchedules.laycan_of_vessel.split(' to ')
      if (laycanParts.length < 2) return false
      
      const laycanStart = this.moment(laycanParts[0], 'DD-MMM-YYYY')
      const laycanEnd = this.moment(laycanParts[1], 'DD-MMM-YYYY')
      const currentDate = this.moment(date)
      
      return currentDate.isBefore(laycanStart, 'day') || currentDate.isAfter(laycanEnd, 'day')
    },
    validateThreeDaysRange(dateRange) {
      if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
        return false
      }
      
      const startDate = this.moment(dateRange[0], 'DD-MMM-YYYY')
      const endDate = this.moment(dateRange[1], 'DD-MMM-YYYY')
      const daysDifference = endDate.diff(startDate, 'days')
      
      return daysDifference === 2
    },
    async cancelEdit() {
      await this.initializeEditableFields(this.tentativeBargeSchedules)
      this.$nextTick(() => {
        this.isEditingDetails = false
      })
    },
    putOutOrFormatValue(data) {
      if (data && isNumber(data)) {
        return data.toLocaleString('id-ID')
      }
      const momentFormatInstance = ['DD-DD/M', 'YYYY-MM-DDTHH:mm:ss.SSSSZ']
      if (data && this.moment(data, momentFormatInstance, true).isValid()) {
        return this.moment(data, momentFormatInstance).format('DD-MMM-YYYY HH:mm')
      }
      return data || '-'
    },
    async updateBlendingSchemeItems(items) {
      try {
        const blendingScheme = items.filter(item => !this.blendingSchemes.find(opt => opt.text === item.text ))
        await this.$store.dispatch('VesselLineUp/postBlendingSchemes', { name: blendingScheme[0].text })
        this.showSuccessNotification(this, 'You have successfully add blending scheme')
        await this.getBlendingSchemes()
      } catch (e) {
        this.showFailedNotification(this, 'Failed to add blending scheme, please try again')
      }
    },
    async editRemark() {
      if (this.isRemarkEdit) {
        try {
          await this.$store.dispatch('VesselLineUp/submitVesselLineUp', {
            id: this.$route.params.id || this.vesselLineUpId,
            form: { remark_tbs: this.tbs.tentativeBargeSchedules.remark_tbs },
            isCreate: false
          })
          this.isRemarkEdit = false
          this.showSuccessNotification(this, 'You have successfully update remarks')
          return
        } catch (e) {
          this.isRemarkEdit = false
          this.showFailedNotification(this, 'Failed to update remarks, please try again')
          return
        }
      }
      this.isRemarkEdit = true
    },
    transformBLU() {
      return this.bargeLineUp.map(item => ({
        id: item.id,
        blending_scheme_id: this.tbs.bargeLineUp.blending_scheme_id[item.id],
        sp: this.tbs.bargeLineUp.sp[item.id] || null,
        lhv: this.tbs.bargeLineUp.lhv[item.id] || null,
        royalty: this.tbs.bargeLineUp.royalty[item.id] || null
      }))
    },
    async submit() {
      try {
        if (this.editableFields.cargo_on_board === null || this.editableFields.cargo_on_board === undefined || this.editableFields.cargo_on_board === '') {
          this.showFailedNotification(this, 'Cargo on Board is required')
          return
        }
        if (!this.editableFields.loading_facility) {
          this.showFailedNotification(this, 'Loading Facility is required')
          return
        }
        if (!this.editableFields.flf_fc_nominate) {
          this.showFailedNotification(this, 'FLF/FC Nominate is required')
          return
        }
        if (!this.editableFields.agency) {
          this.showFailedNotification(this, 'Vessel Agent is required')
          return
        }

        if (this.editableFields.laycan_3_days_range && !this.isLaycanRangeValid) {
          this.showFailedNotification(this, '3 Days LayCan must be exactly 3 days range')
          return
        }

        const formData = {
          cargo_on_board: this.editableFields.cargo_on_board,
          loading_facility_id: this.editableFields.loading_facility,
          flf_fc_nominate_id: this.editableFields.flf_fc_nominate,
          agency_id: this.editableFields.agency
        }

        if(this.editableFields.shipper_rep) {
          formData.shipper_rep = this.editableFields.shipper_rep
        }

        if(this.editableFields.loading_facility === 2) {
          formData.laycan_3_days_start = this.editableFields.laycan_3_days_start,
          formData.laycan_3_days_end = this.editableFields.laycan_3_days_end
        }

        await this.$store.dispatch('TentativeBargeSchedule/putTentativeBargeSchedules', {
          id: this.$route.params.id || this.vesselLineUpId,
          form: formData
        })
        this.showSuccessNotification(this, 'You have successfully update TBS Details')
        this.isEditingDetails = false
        await this.getTentativeBargeSchedules()
      } catch (error) {
        const errorMessage = error?.data?.error?.errors[0][0]?.message || error?.data?.error?.message || 'Failed to update TBS Details, please try again'
        this.showFailedNotification(this, errorMessage)
      }
    },
    getValueStatus(statusText) {
      return this.statusOptions.find(status => status.text === statusText)
    },
    async statusOnInput(id, status) {
      try {
        this.showLoading(this.$refs.dataTable.$el)
        await this.$store.dispatch('BargeLineUp/putBargeLineUp', { id, status })
        this.showSuccessNotification(this, 'You have successfully update status')
        this.closeLoading(this.$refs.dataTable.$el)
      } catch (err) {
        this.closeLoading(this.$refs.dataTable.$el)
        this.showFailedNotification(this, 'Something went wrong')
      }
    },
    showLoading(el) {
      this.$vs.loading({
        type: 'radius',
        color: 'secondary',
        container: el,
        scale: 0.6
      })
    },
    onHandleEdit(row) {
      this.showModalEditTbs = true
      this.modalEditTbs = { ...row }
    },
    closeLoading(el) {
      this.$vs.loading.close(el)
    },
    getThreeDaysLayCan() {
      if (this.tentativeBargeSchedules.tentative_barge_schedule_header) {
        const startDate = this.moment(this.tentativeBargeSchedules.tentative_barge_schedule_header.laycan_3_days_start).format('DD-MMM-YYYY')
        const endDate = this.moment(this.tentativeBargeSchedules.tentative_barge_schedule_header.laycan_3_days_end).format('DD-MMM-YYYY')
        return `${startDate} to ${endDate}`
      }
      return '-'
    },
    isTabActive(tabName) {
      return this.activeTab === tabName
    },
    changeTabTo(tabName) {
      this.activeTab = tabName
    },
    async onExportTBS() {
      try {
        this.isOnExportTBS = true;
        await this.$store.dispatch('TentativeBargeSchedule/exportTBS', { vessel_line_up_id: this.vesselLineUpId }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isOnExportTBS = false;
      } catch (error) {
        this.isDownload = false;
        this.showFailedNotification(this, 'Failed to download TBS, please try again');
      }
    },
    editDataTbs() {
      this.isEditingTbs = true
      this.activeTab = 'TBS Data'
    },
    onCancelEdit() {
      this.isEditingTbs = false
      this.getTentativeBargeSchedules()
    }
  }
}
</script>

<style lang="scss" scoped>

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.bg-header-table {
  background-color: rgba(205, 198, 225, 0.5) !important;
}

.mx-min-3 {
  margin-right: -0.75rem;
  margin-left: -0.75rem;
}

.min-w-cargo {
  min-width: 17.75rem;
}

.icon-shadow {
  -webkit-filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.11));
  filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.11));
  width: 42px;
  height: 42px;
  background-color: #C2B6EA;
  border-radius: 0.5rem;
  padding: 0.5rem;
}

.extra {
  td {
    background-color: rgba(205, 198, 225, 0.5) !important;
  }
}

.button {
  @apply py-4 #{!important};
  @apply rounded-xl;
  @apply border-2;
  @apply font-semibold;
  @apply text-base;
  width: 140px;
  height: calc( 1rem * 10 / 3 );

  &:disabled {
    @apply text-grey-001;
    background-color: #ECEBED !important;
    -webkit-box-shadow: none !important;
    box-shadow: none !important;
  }

  &.shadow-secondary {
    -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
    box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  }

  &:focus {
    @apply outline-none;
  }
}

.bg-icon {
  @apply cursor-pointer;
  @apply rounded-lg;
  @apply w-8;
  @apply h-8;
  background-color: rgba(252, 219, 208, 0.36);
}

.bg-icon-success {
  @apply cursor-pointer;
  @apply rounded-lg;
  @apply w-8;
  @apply h-8;
  background-color: #D5F2EA;
}

/deep/.vs-con-table {
  @apply relative;

  .vs-table {
    &--tr {
      &:not(.activeEdit):not(.tr-expandedx):not(.hoverFlat):hover {
        z-index: unset;
        -webkit-transform: unset;
        transform: unset;
      }
    }
    &--td {
      > span {
        @apply items-center;
      }
    }
  }
}

.custom-select {
  @apply w-form-field;
  height: 2.5rem;

  /deep/.bg-accent-multiselect {
    @apply p-4;
    height: 2.5rem;
  }

  /deep/.bg-option-container {
    min-width: 14.3rem !important;
    max-height: 300px !important;
  }

  /deep/.bg-inner-add-option-container {
    .vs-input {
      width: 12.8rem;
    }
  }

  /deep/.bg-inner-footer-container {
    @apply flex;
    @apply items-center;
    @apply justify-center;

    .vs-button {
      @apply w-full;
      -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
      box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);

      &:focus {
        @apply outline-none;
      }
    }
  }
}

/deep/.bg-accent-multiselect {
  > div {
    > span {
      @apply truncate;
    }
  }
}

.status {
  /deep/.bg-accent-multiselect {
    > div {
      > span {
        @apply font-semibold;
      }
    }
  }
}

.loss-gain-cargo {
  /deep/.vs-input--wrapper {
    max-height: 30px;
  }

  /deep/.vs-input--input {
    @apply py-2 #{!important};
    max-height: 30px;
  }
}
</style>
